/**
 * Script detail — view-only.
 *
 * Scripts are authored in `.deployit/components/<slug>/scripts.yaml` and
 * brought into the DB via the Application page's Discover affordance
 * (issue #27). This page shows what the row currently is and surfaces the
 * Run panel; everything mutating happens through YAML + discovery.
 *
 * If a script row predates discovery (sourceRef=null, legacy SPA-typed),
 * it still shows here — same view, no special treatment. To clean one up,
 * add it to the application's `.deployit/components/<slug>/scripts.yaml`
 * and re-discover; the reconciler will recognise it as an update and bring
 * it under YAML management.
 */
import {
  Terminal,
  Loader2,
  ChevronLeft,
  AlertTriangle,
  Zap,
  Clock4,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Label } from '@/components/ui/Label.js';
import { api, type ScriptReversibility } from '@/api/client.js';
import { cn } from '@/lib/utils.js';
import { ScriptRunPanel } from '@/components/applications/ScriptRunPanel.js';
import { resolveCapabilities } from '@/lib/templates.js';

const REV_OPTIONS: Array<{ value: ScriptReversibility; label: string; dot: string }> = [
  { value: 'yes', label: 'Reversible', dot: 'bg-emerald-500' },
  { value: 'best-effort', label: 'Best-effort', dot: 'bg-amber-500' },
  { value: 'no', label: 'Irreversible', dot: 'bg-red-500' },
];

export function ScriptDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();

  const scriptQuery = useQuery({
    queryKey: ['script', id],
    queryFn: () => api.scripts.get(id!),
    enabled: !!id,
  });
  const script = scriptQuery.data;

  // We need the owning component to render its name and to link back to the
  // application page. The Application lookup happens implicitly via the
  // list-all endpoint, so we don't need to fetch the app directly.
  const componentQuery = useQuery({
    queryKey: ['component', script?.componentId],
    queryFn: () => api.components.get(script!.componentId),
    enabled: !!script,
  });
  const component = componentQuery.data;

  const appsQuery = useQuery({ queryKey: ['applications'], queryFn: () => api.applications.listAll() });
  const application = useMemo(
    () => (component ? (appsQuery.data ?? []).find((a) => a.id === component.applicationId) : undefined),
    [component, appsQuery.data],
  );

  if (scriptQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!script) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <Terminal className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>Script not found, or you don&apos;t have access to it.</p>
          <Button variant="link" onClick={() => navigate({ to: '/p/applications' })}>
            Back to hosting
          </Button>
        </CardContent>
      </Card>
    );
  }

  const ModeIcon = script.executionMode === 'sync' ? Zap : Clock4;
  const reversibility = REV_OPTIONS.find((o) => o.value === script.reversibility);

  return (
    <div className="space-y-8">
      <div>
        {application ? (
          <Link
            to="/p/applications/applications/$slug"
            params={{ slug: application.slug }}
            search={{ org: application.orgSlug }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3 w-3" />
            Back to {application.name}
          </Link>
        ) : (
          <Link
            to="/p/applications"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3 w-3" />
            Back to hosting
          </Link>
        )}
      </div>

      <header className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Terminal className="h-6 w-6" />
        </div>
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{script.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {component && (
              <>
                <span>{component.name}</span>
                <span>·</span>
              </>
            )}
            <span className="font-mono">/{script.slug}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <ModeIcon className="h-3.5 w-3.5" />
              {script.executionMode}
            </span>
          </div>
        </div>
      </header>

      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
        This page is view-only. Scripts are defined in{' '}
        <span className="font-mono">.deployit/components/&lt;slug&gt;/scripts.yaml</span> in the
        application&apos;s repo. To change this script, edit the YAML, commit, and use{' '}
        <span className="font-medium">Discover from repo</span> on the Application page to apply
        the update.
        {script.sourceRef && (
          <>
            {' '}
            <br />
            Sourced from{' '}
            <span className="font-mono">{script.sourceRef.filePath}</span> at sha{' '}
            <span className="font-mono">{script.sourceRef.sha.slice(0, 7)}</span>.
          </>
        )}
      </p>

      {/*
        Run panel is template-gated for symmetry with the Application page —
        SaaS templates suppress script execution entirely. If a script row
        somehow exists under a SaaS app (shouldn't happen, but be defensive)
        the run panel is hidden and the definition cards below stay visible.
      */}
      {application && resolveCapabilities(application.templateId).scripts && (
        <ScriptRunPanel script={script} applicationId={application.id} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Command</CardTitle>
          <CardDescription>
            The exact text we&apos;ll execute, with <span className="font-mono">{'{{param}}'}</span>{' '}
            substitutions applied. Secrets are masked in audit; everything else is logged
            verbatim so an operator can reproduce the run.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="rounded-md border border-border bg-muted/30 p-3 font-mono text-xs">
            {script.commandTemplate}
          </pre>

          <div className="space-y-1.5">
            <Label htmlFor="desc">Description</Label>
            <p id="desc" className="text-sm text-muted-foreground">
              {script.description ?? <span className="italic">No description.</span>}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Safety</CardTitle>
          <CardDescription>
            Reversibility classification and warnings feed the change preview surface before
            this script runs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Reversibility</Label>
            <span className="inline-flex items-center gap-2 text-sm">
              <span className={cn('h-2 w-2 rounded-full', reversibility?.dot)} />
              {reversibility?.label}
            </span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="warnings">Warnings</Label>
            {(script.warnings ?? []).length === 0 ? (
              <p id="warnings" className="text-xs italic text-muted-foreground">
                No warnings declared.
              </p>
            ) : (
              <ul id="warnings" className="space-y-1.5">
                {script.warnings.map((w, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300"
                  >
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="perm">Required permission</Label>
            <span id="perm" className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">
              {script.requiredPermission}
            </span>
          </div>

          {script.allowedEnvironments && (
            <div className="space-y-1.5">
              <Label htmlFor="env-allowlist">Environment allow-list</Label>
              <p id="env-allowlist" className="text-xs">
                {script.allowedEnvironments.kinds &&
                  script.allowedEnvironments.kinds.length > 0 && (
                    <>
                      kind ∈{' '}
                      <span className="font-mono">
                        {`{${script.allowedEnvironments.kinds.join(', ')}}`}
                      </span>
                    </>
                  )}
                {script.allowedEnvironments.kinds?.length &&
                  script.allowedEnvironments.slugs?.length && <span> OR </span>}
                {script.allowedEnvironments.slugs &&
                  script.allowedEnvironments.slugs.length > 0 && (
                    <>
                      slug ∈{' '}
                      <span className="font-mono">
                        {`{${script.allowedEnvironments.slugs.join(', ')}}`}
                      </span>
                    </>
                  )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Execution profile</CardTitle>
          <CardDescription>Timeouts and the UI hint shown next to the run button.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="expected">Expected duration (ms)</Label>
            <p id="expected" className="text-sm">
              {script.expectedDurationMs ?? (
                <span className="italic text-muted-foreground">unset</span>
              )}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timeout">
              {script.executionMode === 'sync'
                ? 'Sync timeout (ms, max 120 000)'
                : 'Queued timeout (ms, max 86 400 000)'}
            </Label>
            <p id="timeout" className="text-sm">
              {(script.executionMode === 'sync'
                ? script.syncTimeoutMs
                : script.queuedTimeoutMs) ?? (
                <span className="italic text-muted-foreground">unset</span>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Parameters</CardTitle>
          <CardDescription>
            JSON Schema describing accepted inputs. The Run panel above renders a form from this;
            the API validates against it on run.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
            {JSON.stringify(script.paramSchema ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
